import { useEffect, useState } from "react";
import {
    Edit,
    Info,
} from '@mui/icons-material';
import {
    Box,
    IconButton,
    Typography,
} from '@mui/material';
import {
    DataGrid,
} from '@mui/x-data-grid';
import { Trans, useTranslation } from 'react-i18next';

import { computeWorkTime } from "../../model/contractInfo";
import DateFormat from "../../utils/DateFormat";
import DurationPickerDialog from "./DurationPickerDialog";
import EditUserWorkingHoursActionItem from "./EditWorkHoursActionItem";
import WorkTimeModelPopover from "./WorkTimeModelPopover";

function EditWorkTimeModelTable({ user, onChange }) {
    const { t } = useTranslation();
    const [openDurationPicker, setOpenDurationPicker] = useState(false);
    const [rows, setRows] = useState([]);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

    useEffect(() => {
        const rows = [
            { id: 'mondayWorkLoad', weekDay: t('montag', { ns: 'userSettingsPage', defaultValue: "Montag" }), workTime: user.contractInfo?.mondayWorkLoad ?? '' },
            { id: 'tuesdayWorkLoad', weekDay: t('dienstag', { ns: 'userSettingsPage', defaultValue: "Dienstag" }), workTime: user.contractInfo?.tuesdayWorkLoad },
            { id: 'wednesdayWorkLoad', weekDay: t('mittwoch', { ns: 'userSettingsPage', defaultValue: "Mittwoch" }), workTime: user.contractInfo?.wednesdayWorkLoad },
            { id: 'thursdayWorkLoad', weekDay: t('donnerstag', { ns: 'userSettingsPage', defaultValue: "Donnerstag" }), workTime: user.contractInfo?.thursdayWorkLoad },
            { id: 'fridayWorkLoad', weekDay: t('freitag', { ns: 'userSettingsPage', defaultValue: "Freitag" }), workTime: user.contractInfo?.fridayWorkLoad },
            { id: 'saturdayWorkLoad', weekDay: t('samstag', { ns: 'userSettingsPage', defaultValue: "Samstag" }), workTime: user.contractInfo?.saturdayWorkLoad },
            { id: 'sundayWorkLoad', weekDay: t('sonntag', { ns: 'userSettingsPage', defaultValue: "Sonntag" }), workTime: user.contractInfo?.sundayWorkLoad },
        ];

        setRows(rows);
    }, [user]);

    const handleCloseDurationPicker = () => {
        setOpenDurationPicker(false);
    };

    const handleDurationSubmit = ({ id, value }) => {
        let contract = user.contractInfo ? {
            ...user.contractInfo,
        } : {
            mondayWorkLoad: 0,
            tuesdayWorkLoad: 0,
            wednesdayWorkLoad: 0,
            thursdayWorkLoad: 0,
            fridayWorkLoad: 0,
            saturdayWorkLoad: 0,
            sundayWorkLoad: 0,
        };
        contract[id] = value;

        let update = {
            ...user,
            contractInfo: {
                ...contract,
            },
        };

        onChange(update);
    };

    const columns = [
        {
            field: 'weekDay',
            headerName: t('wochentag', { ns: 'userSettingsPage', defaultValue: "Wochentag" }),
            width: 200,
            sortable: false,
        }, {
            field: 'workTime',
            headerName: t('stunden', { ns: 'userSettingsPage', defaultValue: "Stunden" }),
            sortable: false,
            width: 150,
            renderCell: (params) => params.row.workTime ? DateFormat.formatDuration(params.row.workTime) : '',
        }, {
            field: 'actions',
            type: 'actions',
            getActions: (params) => {
                return [
                    <EditUserWorkingHoursActionItem
                        handleSubmit={handleDurationSubmit}
                        id={params.row.id}
                        value={params.row.workTime}
                        label='Arbeitszeit bearbeiten'
                        icon={<Edit />}
                    />
                ];
            },
        },
    ];

    const handleChangeWorkTimeTotal = ({ id, value }) => {
        let dailyWorkLoad = Math.trunc(value / 5);

        let contract = user.contractInfo ? {
            ...user.contractInfo,
        } : {
            mondayWorkLoad: 0,
            tuesdayWorkLoad: 0,
            wednesdayWorkLoad: 0,
            thursdayWorkLoad: 0,
            fridayWorkLoad: 0,
            saturdayWorkLoad: 0,
            sundayWorkLoad: 0,
        };

        for (const [key, value] of Object.entries(contract)) {
            if (key !== 'id' && key !== 'saturdayWorkLoad' && key !== 'sundayWorkLoad') {
                contract[key] = dailyWorkLoad;
            };
        };

        let update = {
            ...user,
            contractInfo: {
                ...contract,
            },
        };

        onChange(update);
    };

    const handleOpenPopover = (event) => {
        event.stopPropagation();
        setPopoverAnchorEl(event.currentTarget);
    };

    const handleClosePopover = (event) => {
        event.stopPropagation();
        setPopoverAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 2 }}>
                <Typography sx={{ fontWeight: 'bold' }}>
                    <Trans i18nKey='vertraglicheWochenstunden' ns='userSettingsPage'>Vertragliche Wochenstunden: </Trans>{DateFormat.formatDuration(computeWorkTime(user.contractInfo) ?? 0)}
                </Typography>
                <IconButton onClick={() => setOpenDurationPicker(true)} >
                    <Edit sx={{ height: '20px', width: '20px' }} />
                </IconButton>
            </Box>
            <DurationPickerDialog
                handleSubmit={handleChangeWorkTimeTotal}
                id={'weeklyWorkLoad'}
                value={computeWorkTime(user.contractInfo) ?? 0}
                open={openDurationPicker}
                onClose={handleCloseDurationPicker}
                type={'positive'}
            />
            <Box>
                <Typography sx={{ pb: 2 }}>
                    <Trans i18nKey='TrageArbeitsstundenEin'>Bitte gib das Arbeitszeitmodell ein, wie es durch den Arbeitsvertrag geregelt ist.</Trans>
                    <span>
                        <IconButton onClick={handleOpenPopover} sx={{ height: '20px', width: '20px', verticalAlign: 'top', mx: .5 }}>
                            <Info sx={{ height: '20px', width: '20px' }} />
                        </IconButton>
                    </span>
                </Typography>
            </Box>
            <WorkTimeModelPopover
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            />
            <DataGrid
                rows={rows}
                columns={columns}
                editMode="row"
                disableColumnMenu
                disableColumnFilter
                sx={{ width: 'fit-content' }}
                hideFooter={true}
            />
        </>
    );
};

export default EditWorkTimeModelTable
